import axios from 'axios';

import { API } from '../constants/api';

import { axiosInterceptor } from './interceptors';

export const listQuote = async (params: any, signal?: any) => {
    const response = await axiosInterceptor.get(API.QUOTE.LIST, {
        params,
        signal,
    });

    return response;
};

export const getQuote = async (id: number) => {
    const response = await axiosInterceptor.get(`${API.QUOTE.GET}/${id}`);

    return response;
};

export const getUserQuotes = async () => {
    const response = await axiosInterceptor.get(API.QUOTE.GET_USER_QUOTES);

    return response;
};

export const createQuote = async (data: any) => {
    const response = await axiosInterceptor.post(API.QUOTE.CREATE, data);

    return response;
};

// Multi-stop submit (Task 8) — POST /quotes/multi-stop. Unlike createQuote() above (which returns
// the raw axios response), this returns response.data directly: the BE's sendResponse() wraps the
// body as { status, message, data: { quoteId, code, stops } }, so callers read res.data.quoteId.
export const createMultiStopQuote = async (data: any) => {
    const response = await axiosInterceptor.post(API.QUOTE.MULTI_STOP, data);

    return response.data;
};

export const updateQuote = async (id: string, data: any) => {
    const response = await axiosInterceptor.put(`${API.QUOTE.UPDATE}/${id}`, data);

    return response;
};

export const updatePickupDate = async (id: string, pickupDate: string, pickupEndDate?: string | null) => {
    const response = await axiosInterceptor.patch(`${API.QUOTE.UPDATE_PICKUP_DATE}/${id}`, {
        pickupDate,
        ...(pickupEndDate ? { pickupEndDate } : {}),
    });

    return response;
};

export const getQuoteDetail = async (data: any) => {
    const response = await axiosInterceptor.post(API.QUOTE.QUOTE_DETAIL, data);

    return response;
};

export const activeQuote = async (id: number, data: any) => {
    const response = await axiosInterceptor.post(`${API.QUOTE.ACTIVE_QUOTE}/${id}`, data);

    return response;
};

export const getActiveQuotes = async (id: number) => {
    const response = await axiosInterceptor.get(`${API.QUOTE.ACTIVE_QUOTE}/${id}`);

    return response;
};

export const updateStatus = async (id: number) => {
    const response = await axiosInterceptor.patch(`${API.QUOTE.UPDATE_STATUS}/${id}`);

    return response;
};

export const updateQuoteStatus = async (id: number) => {
    const response = await axiosInterceptor.patch(`${API.QUOTE.UPDATE_QUOTE_STATUS}/${id}`);

    return response;
};

// Adds a cache-busting query param to avoid returning a cached asset when desired
const appendCacheBuster = (url: string, buster: string | number) => {
    try {
        const hasQuery = url.includes('?');
        const separator = hasQuery ? '&' : '?';

        return `${url}${separator}cb=${encodeURIComponent(String(buster))}`;
    } catch {
        return url;
    }
};

export const downloadGCSPdf = async (
    url: string,
    options?: {
        isUseCache?: boolean;
        cacheBuster?: string | number;
        signal?: AbortSignal;
        headers?: Record<string, string>;
    }
) => {
    const { isUseCache = true, cacheBuster, signal, headers } = options || {};

    const noCacheHeaders = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
    } as Record<string, string>;

    const requestUrl = isUseCache ? url : appendCacheBuster(url, cacheBuster ?? Date.now());

    const response = await axios.get(requestUrl, {
        responseType: 'blob',
        signal,
        headers: isUseCache ? headers : { ...noCacheHeaders, ...(headers || {}) },
    });

    return response;
};

export const uploadCustomsDocuments = async (data: any) => {
    const response = await axiosInterceptor.post(API.QUOTE.UPLOAD_DOCUMENTS, data);

    return response;
};

export const bookQuote = async (data: any) => {
    const response = await axiosInterceptor.post(API.QUOTE.BOOK_QUOTE, data);

    return response;
};

// Quote list count for tab badges
export const listQuoteCount = async (params: any, signal?: any) => {
    const response = await axiosInterceptor.get(API.QUOTE.LIST_COUNT, {
        params,
        signal,
    });

    return response;
};

// Custom view CRUD
export const getQuoteViewList = async (params: any) => {
    const response = await axiosInterceptor.get(API.VIEW.LIST, { params });

    return response;
};

export const createQuoteView = async (data: any) => {
    const response = await axiosInterceptor.post(API.VIEW.CREATE, data);

    return response;
};

export const updateQuoteView = async (id: string, data: any) => {
    const response = await axiosInterceptor.patch(`${API.VIEW.UPDATE}/${id}`, data);

    return response;
};

export const deleteQuoteView = async (id: number) => {
    const response = await axiosInterceptor.delete(`${API.VIEW.DELETE}/${id}`);

    return response;
};

// Quote activity feed — always uses entityType + entityId query params
export const getQuoteActivities = async (params: { entityType: 'quote' | 'order'; entityId: number; perPage?: number; lastId?: number | null }) => {
    const response = await axiosInterceptor.get(`${API.QUOTE.ACTIVITIES}/activities`, { params });

    return response;
};

export const postQuoteActivity = async (entityType: 'quote' | 'order', entityId: number, formData: FormData) => {
    const response = await axiosInterceptor.post(`${API.QUOTE.ACTIVITIES}/activities`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { entityType, entityId },
    });

    return response;
};

// Table column settings
export const updateTableColumnSettings = async (data: any) => {
    const response = await axiosInterceptor.patch(API.TABLE_COLUMN_SETTINGS.UPDATE, data);

    return response;
};

export const getTableColumnSettings = async (params: any) => {
    const response = await axiosInterceptor.get(API.TABLE_COLUMN_SETTINGS.GET, { params });

    return response;
};

export const createQuoteCustomerRequest = async (quoteId: number, data: { requestType: 're_quote' | 'duplicate'; quoteCarrierId?: number; description?: string }) => {
    const response = await axiosInterceptor.post(`${API.QUOTE.CUSTOMER_REQUEST}/${quoteId}`, data);

    return response;
};

export const getInitData = async () => {
    const response = await axiosInterceptor.get(API.COMMON.INIT);

    return response;
};
